import { ENALYSIS_TREND_LIST_SOURCE } from "@/constants/enalysis-trends-constants.js";

// --- shared -----------------------------------------------------------------

/** Red band past scale ends: 5% of limit, at least 1. */
export function unitSetupGaugeBarPadding(scaleMax) {
  const sm = Number(scaleMax);
  if (!(sm > 0)) return 1;
  return Math.max(sm * 0.05, 1);
}

// --- discharge / suction pressure -------------------------------------------

/**
 * scaleMax = min(Cylinder MAWP, Piping MAWP, Cooler design MAWP, PSV setting) for the row's stage/throw.
 */
export function computeDischargePressureScaleMax(
  stageNum,
  throwNum,
  cylinders,
  designConditions,
  aerialCoolerDetails
) {
  const s = Number(stageNum);
  const t = Number(throwNum);
  if (!Number.isFinite(s) || !Number.isFinite(t)) return null;

  const cylArr = cylinders ?? [];
  const designArr = designConditions ?? [];
  const aerialArr = aerialCoolerDetails ?? [];

  const cylinderRow = cylArr.find(
    (c) => Number(c?.stage) === s && Number(c?.throw) === t
  );
  const designRow = designArr.find((d) => Number(d?.stage) === s);
  const aerialRow = aerialArr.find((a) => Number(a?.stage) === s);

  const candidates = [
    cylinderRow?.cylinderMAWP,
    designRow?.dischargePipingMawp,
    aerialRow?.designPressureMAWP,
    designRow?.pressureSafetyValveSetting,
  ].filter((x) => x != null);

  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

/**
 * @param {unknown} p
 * @param {number} scaleMax
 * @param {"dischargePressureUnitSetup"|"suctionPressureUnitSetup"} gaugeLayout
 */
function withPressureUnitSetupGauge(p, scaleMax, gaugeLayout) {
  const hi = 0.9 * scaleMax;
  const hihi = 0.95 * scaleMax;
  const displayMin = -unitSetupGaugeBarPadding(scaleMax);
  return {
    ...p,
    displayType: "Gauge",
    apiData: {
      ...p.apiData,
      minValue: 0,
      maxValue: scaleMax,
      displayMin,
      gaugeLayout,
      hiPressure: hi,
      hihiPressure: hihi,
    },
    alarmSetPoints: {
      lolo: null,
      lo: null,
      hi,
      hihi,
    },
  };
}

/**
 * @param {unknown[]} parameters
 * @param {{ cylinders?: unknown[], designConditions?: unknown[], aerialCoolers?: object|null }} enrichmentContext
 */
export function enrichDischargePressureGaugeParameters(
  parameters,
  enrichmentContext
) {
  const cylinders = enrichmentContext?.cylinders;
  const designConditions = enrichmentContext?.designConditions;
  const aerialDetails = enrichmentContext?.aerialCoolers?.aerialCoolerDetails;

  return parameters.map((p) => {
    if (p?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return p;
    if (p.apiData.baseTrendIdentifier !== "dischargePressure") return p;
    const sn = p.apiData.stageNum;
    const tn = p.apiData.throwNum;
    if (sn == null || tn == null) return p;

    const scaleMax = computeDischargePressureScaleMax(
      sn,
      tn,
      cylinders,
      designConditions,
      aerialDetails
    );
    if (scaleMax == null) return p;

    return withPressureUnitSetupGauge(p, scaleMax, "dischargePressureUnitSetup");
  });
}

/** Stage 1 suction uses discharge limits for stage 1; stage N>1 uses stage N−1 (same throw). */
export function referenceStageForSuctionPressureGauge(stageNum) {
  const s = Number(stageNum);
  if (!Number.isFinite(s) || s < 1) return null;
  return s === 1 ? 1 : s - 1;
}

/**
 * @param {unknown[]} parameters
 * @param {{ cylinders?: unknown[], designConditions?: unknown[], aerialCoolers?: object|null }} enrichmentContext
 */
export function enrichSuctionPressureGaugeParameters(
  parameters,
  enrichmentContext
) {
  const cylinders = enrichmentContext?.cylinders;
  const designConditions = enrichmentContext?.designConditions;
  const aerialDetails = enrichmentContext?.aerialCoolers?.aerialCoolerDetails;

  return parameters.map((p) => {
    if (p?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return p;
    if (p.apiData.baseTrendIdentifier !== "suctionPressure") return p;
    const sn = p.apiData.stageNum;
    const tn = p.apiData.throwNum;
    if (sn == null || tn == null) return p;

    const refStage = referenceStageForSuctionPressureGauge(sn);
    if (refStage == null) return p;

    const scaleMax = computeDischargePressureScaleMax(
      refStage,
      tn,
      cylinders,
      designConditions,
      aerialDetails
    );
    if (scaleMax == null) return p;

    return withPressureUnitSetupGauge(p, scaleMax, "suctionPressureUnitSetup");
  });
}

// --- discharge temperature --------------------------------------------------

const DISCHARGE_TEMP_HI_OFFSET_F = 10;

export function computeDischargeTemperatureScaleMax(
  stageNum,
  designConditions,
  aerialCoolerDetails
) {
  const s = Number(stageNum);
  if (!Number.isFinite(s)) return null;

  const designArr = designConditions ?? [];
  const aerialArr = aerialCoolerDetails ?? [];

  const designRow = designArr.find((d) => Number(d?.stage) === s) ?? null;
  const aerialRow = aerialArr.find((a) => Number(a?.stage) === s) ?? null;

  const candidates = [
    designRow?.maxPipingBottleTemp,
    designRow?.maxValveTemp,
    aerialRow?.maxDesignTemperature,
  ].filter((x) => x != null);

  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

/**
 * @param {unknown[]} parameters
 * @param {{ designConditions?: unknown[], aerialCoolers?: object|null }} enrichmentContext
 */
export function enrichDischargeTemperatureGaugeParameters(
  parameters,
  enrichmentContext
) {
  const designConditions = enrichmentContext?.designConditions;
  const aerialDetails = enrichmentContext?.aerialCoolers?.aerialCoolerDetails;

  return parameters.map((p) => {
    if (p?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return p;
    if (p.apiData.baseTrendIdentifier !== "dischargeTemperature") return p;
    const sn = p.apiData.stageNum;
    if (sn == null) return p;

    const scaleMax = computeDischargeTemperatureScaleMax(
      sn,
      designConditions,
      aerialDetails
    );
    if (scaleMax == null) return p;

    const hi = Math.max(0, scaleMax - DISCHARGE_TEMP_HI_OFFSET_F);
    const hihi = scaleMax;
    const pad = unitSetupGaugeBarPadding(scaleMax);

    return {
      ...p,
      displayType: "Gauge",
      apiData: {
        ...p.apiData,
        minValue: 0,
        maxValue: scaleMax,
        displayMin: -pad,
        displayMax: scaleMax + pad,
        gaugeLayout: "dischargeTemperatureUnitSetup",
      },
      alarmSetPoints: {
        lolo: null,
        lo: null,
        hi,
        hihi,
      },
    };
  });
}

// --- blowby -----------------------------------------------------------------

function isDoubleActingCylinder(row) {
  const s = String(row?.cylinderAction ?? "").trim().toLowerCase();
  return s === "da" || s.includes("double");
}

function blowbyWarningSeverePair(row, warningKey, severeKey) {
  if (!row) return null;
  const hi = row[warningKey];
  const hihi = row[severeKey];
  if (!(hi < hihi)) return null;
  return { hi, hihi, scaleMax: hihi };
}

function readBlowbyUnitSetupThresholds(row) {
  if (!row) return null;
  const deviation = blowbyWarningSeverePair(
    row,
    "blowbyDeviationThresholdWarning",
    "blowbyDeviationThresholdSevere"
  );
  if (isDoubleActingCylinder(row)) {
    return (
      blowbyWarningSeverePair(
        row,
        "doubleActingBlowbyWarningThreshold",
        "doubleActingBlowbySevereThreshold"
      ) ?? deviation
    );
  }
  return (
    blowbyWarningSeverePair(
      row,
      "singleActingBlowbyWarningThreshold",
      "singleActingBlowbySevereThreshold"
    ) ?? deviation
  );
}

function findCylinderRowForBlowby(stageNum, throwNum, cylinders) {
  const s = Number(stageNum);
  const t = Number(throwNum);
  if (!Number.isFinite(s) || !Number.isFinite(t)) return null;
  return (cylinders ?? []).find(
    (c) => Number(c?.stage) === s && Number(c?.throw) === t
  ) ?? null;
}

/**
 * @param {unknown[]} parameters
 * @param {{ cylinders?: unknown[] }} enrichmentContext
 */
export function enrichBlowbyGaugeParameters(parameters, enrichmentContext) {
  const cylinders = enrichmentContext?.cylinders;

  return parameters.map((p) => {
    if (p?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return p;
    if (p.apiData.baseTrendIdentifier !== "blowby") return p;
    const sn = p.apiData.stageNum;
    const tn = p.apiData.throwNum;
    if (sn == null || tn == null) return p;

    const cylRow = findCylinderRowForBlowby(sn, tn, cylinders);
    const limits = readBlowbyUnitSetupThresholds(cylRow);
    if (limits == null) return p;

    const { hi, hihi, scaleMax } = limits;
    const pad = unitSetupGaugeBarPadding(scaleMax);

    return {
      ...p,
      displayType: "Gauge",
      apiData: {
        ...p.apiData,
        minValue: 0,
        maxValue: scaleMax,
        displayMin: -pad,
        displayMax: scaleMax + pad,
        gaugeLayout: "blowbyUnitSetup",
      },
      alarmSetPoints: {
        lolo: null,
        lo: null,
        hi,
        hihi,
      },
    };
  });
}

// --- clearance --------------------------------------------------------------

const CLEARANCE_MAX_FIELD = {
  hePocketAdjustment: "maxPocketTravelInches",
  headEndSpacersAdded: "maxHeClearance",
  crankEndSpacersAdded: "maxCeClearance",
};

function findClearanceRow(rows, stageNum, throwNum) {
  const s = Number(stageNum);
  const t = Number(throwNum);
  if (!Number.isFinite(s) || !Number.isFinite(t)) return null;
  return (
    rows.find((r) => Number(r?.stage) === s && Number(r?.throw) === t) ?? null
  );
}

/**
 * @param {unknown[]} parameters
 * @param {{ clearances?: unknown }} enrichmentContext
 */
export function enrichClearanceGaugeParameters(parameters, enrichmentContext) {
  const rows = enrichmentContext?.clearances ?? [];

  return parameters.map((p) => {
    if (p?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return p;
    const field = CLEARANCE_MAX_FIELD[p.apiData.baseTrendIdentifier];
    if (!field) return p;
    const sn = p.apiData.stageNum;
    const tn = p.apiData.throwNum;
    if (sn == null || tn == null) return p;

    const row = findClearanceRow(rows, sn, tn);
    const maxGreen = row?.[field];
    if (maxGreen == null) return p;

    const displayMin = -Math.max(maxGreen * 0.05, 0.01);
    const displayMax = maxGreen + Math.max(maxGreen * 0.05, 0.01);

    return {
      ...p,
      displayType: "Gauge",
      apiData: {
        ...p.apiData,
        minValue: 0,
        maxValue: maxGreen,
        displayMin,
        displayMax,
        gaugeLayout: "clearanceZeroToMax",
      },
      alarmSetPoints: {
        lolo: null,
        lo: null,
        hi: null,
        hihi: null,
      },
    };
  });
}

// --- pipeline ---------------------------------------------------------------

/**
 * @param {unknown[]} parameters
 * @param {{ cylinders?: unknown, designConditions?: unknown, aerialCoolers?: object|null, clearances?: unknown }} [enrichmentContext]
 */
export function applyEnalysisTrendGaugeEnrichments(
  parameters,
  enrichmentContext
) {
  const enrichment = enrichmentContext ?? {};
  return enrichClearanceGaugeParameters(
    enrichBlowbyGaugeParameters(
      enrichDischargeTemperatureGaugeParameters(
        enrichSuctionPressureGaugeParameters(
          enrichDischargePressureGaugeParameters(parameters, enrichment),
          enrichment
        ),
        enrichment
      ),
      enrichment
    ),
    enrichment
  );
}
